/**
 * Distillery form component for create/edit
 */

import { useForm } from 'react-hook-form';
import { Button, Input, Select } from '../common';
import { COUNTRIES } from '../../utils/constants';
import './DistilleryForm.css';

/**
 * Form for creating or editing a distillery
 * @param {Object} props - Component props
 * @param {Object} props.distillery - Existing distillery data for editing
 * @param {Function} props.onSubmit - Submit handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {boolean} props.loading - Loading state
 * @returns {React.ReactElement} Form element
 */
export default function DistilleryForm({
  distillery,
  onSubmit,
  onCancel,
  loading = false
}) {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: distillery || {
      distillery_name: '',
      country: '',
      address: '',
      city: '',
      state_region: '',
      year_founded: '',
      website_url: '',
      notes: ''
    }
  });

  const currentYear = new Date().getFullYear();

  const onFormSubmit = (data) => {
    const processedData = {
      ...data,
      year_founded: data.year_founded ? parseInt(data.year_founded, 10) : null
    };
    onSubmit(processedData);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="distillery-form">
      <div className="distillery-form__grid">
        <Input
          label="Distillery Name"
          required
          error={errors.distillery_name?.message}
          {...register('distillery_name', {
            required: 'Distillery name is required'
          })}
        />

        <Select
          label="Country"
          required
          options={COUNTRIES}
          error={errors.country?.message}
          {...register('country', {
            required: 'Country is required'
          })}
        />

        <Input
          label="Address"
          error={errors.address?.message}
          {...register('address')}
        />

        <Input
          label="City"
          error={errors.city?.message}
          {...register('city')}
        />

        <Input
          label="State / Region"
          error={errors.state_region?.message}
          {...register('state_region')}
        />

        <Input
          label="Year Founded"
          type="number"
          min="1600"
          max={currentYear}
          error={errors.year_founded?.message}
          {...register('year_founded', {
            min: {
              value: 1600,
              message: 'Year must be after 1600'
            },
            max: {
              value: currentYear,
              message: `Year cannot be after ${currentYear}`
            }
          })}
        />

        <Input
          label="Website URL"
          type="url"
          placeholder="https://example.com"
          error={errors.website_url?.message}
          {...register('website_url', {
            pattern: {
              value: /^https?:\/\/.+/,
              message: 'Must be a valid URL starting with http:// or https://'
            }
          })}
        />

        <div className="distillery-form__full-width input-group">
          <label htmlFor="input-notes" className="input-group__label">Notes</label>
          <textarea
            id="input-notes"
            className="input-group__input"
            rows={4}
            {...register('notes')}
          />
        </div>
      </div>

      <div className="distillery-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" loading={loading}>
          {distillery ? 'Save Changes' : 'Create Distillery'}
        </Button>
      </div>
    </form>
  );
}
